import type { VADConfig, VADCallbacks, VADProcessor } from "./types";

export class WebAudioVAD implements VADProcessor {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private microphone: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private stream: MediaStream | null = null;

  private config: Required<VADConfig>;
  private callbacks: VADCallbacks;

  private isSpeaking = false;
  private silenceStart = 0;
  private speechStart = 0;
  private volumeHistory: number[] = [];
  private audioBuffer: Float32Array[] = [];

  constructor(config: VADConfig = {}, callbacks: VADCallbacks = {}) {
    this.config = {
      threshold: config.threshold ?? 0.02,
      silenceTimeout: config.silenceTimeout ?? 1000,
      minSpeechDuration: config.minSpeechDuration ?? 250,
      sampleRate: config.sampleRate ?? 16000,
      fftSize: config.fftSize ?? 512,
    };
    this.callbacks = callbacks;
  }

  async start(): Promise<void> {
    try {
      // マイクアクセス許可を取得
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: this.config.sampleRate,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Web Audio APIのセットアップ
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: this.config.sampleRate,
      });

      // アナライザーノード（音量分析用）
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = this.config.fftSize;
      this.analyser.smoothingTimeConstant = 0.8;

      // マイク入力を接続
      this.microphone = this.audioContext.createMediaStreamSource(this.stream);

      // スクリプトプロセッサー（音声データ処理用）
      const bufferSize = 4096;
      this.processor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);

      // 音声処理
      this.processor.onaudioprocess = (event) => {
        const inputData = event.inputBuffer.getChannelData(0);
        this.processAudio(inputData);
      };

      // ノードを接続
      this.microphone.connect(this.analyser);
      this.analyser.connect(this.processor);
      this.processor.connect(this.audioContext.destination);
    } catch (error) {
      this.stop();
      throw new Error(`Failed to start VAD: ${error}`);
    }
  }

  stop(): void {
    // 音声処理を停止
    if (this.processor) {
      this.processor.disconnect();
      this.processor.onaudioprocess = null;
      this.processor = null;
    }

    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    if (this.microphone) {
      this.microphone.disconnect();
      this.microphone = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    // 状態をリセット
    this.isSpeaking = false;
    this.audioBuffer = [];
  }

  isActive(): boolean {
    return this.audioContext !== null && this.audioContext.state === "running";
  }

  getVolume(): number {
    if (!this.analyser) return 0;

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(dataArray);

    // 平均音量を計算
    const sum = dataArray.reduce((acc, val) => acc + val, 0);
    return sum / dataArray.length / 255;
  }

  private processAudio(inputData: Float32Array): void {
    // 音量計算（RMS - Root Mean Square）
    let sum = 0;
    for (let i = 0; i < inputData.length; i++) {
      sum += inputData[i] * inputData[i];
    }
    const rms = Math.sqrt(sum / inputData.length);

    // 音量履歴を更新（スムージング用）
    this.volumeHistory.push(rms);
    if (this.volumeHistory.length > 10) {
      this.volumeHistory.shift();
    }

    // 平均音量
    const avgVolume = this.volumeHistory.reduce((a, b) => a + b) / this.volumeHistory.length;

    // 音量変化を通知
    if (this.callbacks.onVolumeChange) {
      this.callbacks.onVolumeChange(avgVolume);
    }

    const now = Date.now();

    // 音声検出ロジック
    if (avgVolume > this.config.threshold) {
      if (!this.isSpeaking) {
        // 音声開始
        this.isSpeaking = true;
        this.speechStart = now;
        this.audioBuffer = [];

        if (this.callbacks.onSpeechStart) {
          this.callbacks.onSpeechStart();
        }
      }

      // 音声データをバッファに追加
      this.audioBuffer.push(new Float32Array(inputData));
      this.silenceStart = now;
    } else if (this.isSpeaking) {
      // 音声中の無音
      this.audioBuffer.push(new Float32Array(inputData));

      if (now - this.silenceStart > this.config.silenceTimeout) {
        // 無音タイムアウト - 音声終了
        const speechDuration = now - this.speechStart;

        if (speechDuration >= this.config.minSpeechDuration) {
          // 最小音声長を満たしている場合のみ音声として扱う
          if (this.callbacks.onSpeechEnd) {
            const mergedAudio = this.mergeAudioBuffers(this.audioBuffer);
            this.callbacks.onSpeechEnd(mergedAudio);
          }
        }

        this.isSpeaking = false;
        this.audioBuffer = [];
      }
    }
  }

  private mergeAudioBuffers(buffers: Float32Array[]): Float32Array {
    const totalLength = buffers.reduce((acc, buffer) => acc + buffer.length, 0);
    const result = new Float32Array(totalLength);

    let offset = 0;
    for (const buffer of buffers) {
      result.set(buffer, offset);
      offset += buffer.length;
    }

    return result;
  }
}

// シンプルなファクトリー関数
export function createVAD(config?: VADConfig, callbacks?: VADCallbacks): VADProcessor {
  return new WebAudioVAD(config, callbacks);
}
